/**
 * Mixer Store (Zustand)
 * Manages per-track volume, mute, solo, nudge
 */

import { create } from "zustand";

export interface MixerTrackState {
  mute: boolean;
  solo: boolean;
  gain: number;
  nudgeMs: number;
}

export interface MixerStore {
  masterGain: number;
  trackParams: Map<string, MixerTrackState>;

  setMasterGain: (gain: number) => void;
  setTrackMute: (trackId: string, muted: boolean) => void;
  setTrackSolo: (trackId: string, soloed: boolean) => void;
  setTrackGain: (trackId: string, gain: number) => void;
  setTrackNudge: (trackId: string, nudgeMs: number) => void;
  removeTrack: (trackId: string) => void;
}

export const useMixerStore = create<MixerStore>((set) => ({
  masterGain: 0.8,
  trackParams: new Map(),

  setMasterGain: (gain) => set({ masterGain: gain }),

  setTrackMute: (trackId, muted) =>
    set((state) => {
      const params = new Map(state.trackParams);
      const existing = params.get(trackId) || {
        mute: false,
        solo: false,
        gain: 1,
        nudgeMs: 0,
      };
      params.set(trackId, { ...existing, mute: muted });
      return { trackParams: params };
    }),

  setTrackSolo: (trackId, soloed) =>
    set((state) => {
      const params = new Map(state.trackParams);
      const existing = params.get(trackId) || {
        mute: false,
        solo: false,
        gain: 1,
        nudgeMs: 0,
      };
      params.set(trackId, { ...existing, solo: soloed });
      return { trackParams: params };
    }),

  setTrackGain: (trackId, gain) =>
    set((state) => {
      const params = new Map(state.trackParams);
      const existing = params.get(trackId) || {
        mute: false,
        solo: false,
        gain: 1,
        nudgeMs: 0,
      };
      params.set(trackId, { ...existing, gain });
      return { trackParams: params };
    }),

  setTrackNudge: (trackId, nudgeMs) =>
    set((state) => {
      const params = new Map(state.trackParams);
      const existing = params.get(trackId) || {
        mute: false,
        solo: false,
        gain: 1,
        nudgeMs: 0,
      };
      params.set(trackId, { ...existing, nudgeMs });
      return { trackParams: params };
    }),

  removeTrack: (trackId) =>
    set((state) => {
      const params = new Map(state.trackParams);
      params.delete(trackId);
      return { trackParams: params };
    }),
}));
